'use client';

import { useCallback } from 'react';
import { type MotionValue, useScroll } from 'framer-motion';
import { useScrollContainer } from '@/contexts/layout';
import { useViewport } from '@/hooks/dom';
import { Viewport } from '@/theme/breakpoints';
import {
  HERO_BACKGROUND_ENTRANCE_COMPLETE_THRESHOLD,
  HERO_BACKGROUND_PRISM_DESKTOP,
  HERO_BACKGROUND_PRISM_MOBILE,
  type HeroBackgroundPrismConfig,
} from './Cherobackground.constants';
import { useCherobackgroundMotionValues } from './useCherobackgroundMotionValues';

export interface CherobackgroundMotionState {
  entryProgress: MotionValue<number>;
  glow: MotionValue<number>;
  isEntranceComplete: () => boolean;
  isMobile: boolean;
  prismConfig: HeroBackgroundPrismConfig;
  scale: MotionValue<number>;
}

export function useCherobackgroundMotion(): CherobackgroundMotionState {
  const { containerRef } = useScrollContainer();
  const { viewport } = useViewport();
  const isMobile = viewport === Viewport.Mobile;
  const prismConfig = isMobile ? HERO_BACKGROUND_PRISM_MOBILE : HERO_BACKGROUND_PRISM_DESKTOP;
  const { scrollY } = useScroll({
    container: containerRef || undefined,
  });

  const { entryProgress, glow, scale } = useCherobackgroundMotionValues({
    prismConfig,
    scrollY,
  });

  // Health gate: keep the animated Prism while it runs smoothly, fall back to the static
  // background on a real fault or sustained low FPS. The entrance ramp is excluded from
  // judgement so its deliberate scale/glow animation isn't mistaken for bad frames.
  const isEntranceComplete = useCallback(
    () => entryProgress.get() >= HERO_BACKGROUND_ENTRANCE_COMPLETE_THRESHOLD,
    [entryProgress],
  );

  return {
    entryProgress,
    glow,
    isEntranceComplete,
    isMobile,
    prismConfig,
    scale,
  };
}
